import React, {
  useState,
  useEffect,
  useContext,
  useRef,
  useCallback,
} from "react";
import { Navigate } from "react-router-dom";
import Select from "react-select";
import "../../../../App.css";
import Navbar from "../../../ui/Navbar";
import Modal from "../../../ui/Modal";
import Loader from "../../../ui/Loader";
import { AuthContext } from "../../../../context/AuthContext";
import {
  swalError,
  swalSuccess,
  swalConfirm,
  swalWarning,
} from "../../../../utils/swalAlert";
import { fetchWithAuth } from "../../../../utils/api";
import certificateService from "../../../../services/certificateService";

const serverUrl = process.env.REACT_APP_SERVER_URL || "http://localhost:5000";

const FONT_FAMILIES = [
  {
    value: "Montserrat",
    label: "Montserrat (Modern Clean)",
    css: "'Montserrat', sans-serif",
  },
  {
    value: "Inter",
    label: "Inter (Neutral Clean)",
    css: "'Inter', sans-serif",
  },
  {
    value: "Roboto",
    label: "Roboto (Standard Sans)",
    css: "'Roboto', sans-serif",
  },
  {
    value: "Outfit",
    label: "Outfit (Modern Geometric)",
    css: "'Outfit', sans-serif",
  },
  {
    value: "Playfair Display",
    label: "Playfair Display (Classic Luxury Serif)",
    css: "'Playfair Display', Georgia, serif",
  },
  {
    value: "Cinzel",
    label: "Cinzel (Formal Diplomatic Serif)",
    css: "'Cinzel', 'Times New Roman', serif",
  },
  {
    value: "Merriweather",
    label: "Merriweather (Refined Serif)",
    css: "'Merriweather', Georgia, serif",
  },
  {
    value: "Great Vibes",
    label: "Great Vibes (Formal Calligraphy Script)",
    css: "'Great Vibes', cursive",
  },
  {
    value: "Alex Brush",
    label: "Alex Brush (Flowing Ribbon Script)",
    css: "'Alex Brush', cursive",
  },
  {
    value: "Parisienne",
    label: "Parisienne (French Script)",
    css: "'Parisienne', cursive",
  },
  {
    value: "Pinyon Script",
    label: "Pinyon Script (Vintage Calligraphic)",
    css: "'Pinyon Script', cursive",
  },
  {
    value: "Dancing Script",
    label: "Dancing Script (Casual Script)",
    css: "'Dancing Script', cursive",
  },
  {
    value: "Georgia",
    label: "Georgia (Traditional Serif)",
    css: "Georgia, serif",
  },
  {
    value: "Times New Roman",
    label: "Times New Roman (Academic Serif)",
    css: "'Times New Roman', Times, serif",
  },
  { value: "Arial", label: "Arial (Standard Sans)", css: "Arial, sans-serif" },
  {
    value: "Courier New",
    label: "Courier New (Monospace)",
    css: "'Courier New', Courier, monospace",
  },
];

export const getFontFamilyCss = (fontFamilyName) => {
  const matched = FONT_FAMILIES.find(
    (f) => f.value.toLowerCase() === (fontFamilyName || "").toLowerCase(),
  );
  return matched?.css || `'${fontFamilyName}', sans-serif`;
};

export const getFullImageUrl = (url) => {
  if (!url) return "";
  if (
    url.startsWith("http://") ||
    url.startsWith("https://") ||
    url.startsWith("blob:") ||
    url.startsWith("data:")
  ) {
    return url;
  }
  const cleanServerUrl = serverUrl.replace(/\/+$/, "");
  const cleanPath = url.replace(/^\/+/, "");
  return `${cleanServerUrl}/${cleanPath}`;
};

const PRESET_COLORS = [
  { label: "Dark Navy", value: "#1a1a2e" },
  { label: "Black", value: "#000000" },
  { label: "Brand Orange", value: "#f09a21" },
  { label: "Brand Berry", value: "#da1c5c" },
  { label: "Brand Green", value: "#8dc63f" },
  { label: "Brand Red", value: "#ef4136" },
  { label: "Royal Blue", value: "#1e40af" },
  { label: "Dark Emerald", value: "#065f46" },
  { label: "Deep Purple", value: "#581c87" },
  { label: "Warm Gold", value: "#b45309" },
];

const selectCustomStyles = {
  control: (base, state) => ({
    ...base,
    borderRadius: "8px",
    borderColor: state.isFocused ? "var(--color-1, #f09a21)" : "#d1d5db",
    minHeight: "40px",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(240, 154, 33, 0.2)" : "none",
    "&:hover": {
      borderColor: "var(--color-1, #f09a21)",
    },
    fontSize: "0.9rem",
    backgroundColor: "#ffffff",
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "var(--color-1, #f09a21)"
      : state.isFocused
        ? "#fef3c7"
        : "#ffffff",
    color: state.isSelected ? "#ffffff" : "#1f2937",
    fontSize: "0.9rem",
    padding: "8px 12px",
    cursor: "pointer",
  }),
  menu: (base) => ({
    ...base,
    borderRadius: "8px",
    boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)",
    zIndex: 9999,
  }),
};

const fontSelectCustomStyles = {
  ...selectCustomStyles,
  option: (base, state) => ({
    ...selectCustomStyles.option(base, state),
    fontFamily: state.data?.css || "inherit",
    fontSize: "0.95rem",
  }),
  singleValue: (base, state) => ({
    ...base,
    fontFamily: state.data?.css || "inherit",
    fontSize: "0.95rem",
  }),
};

export default function ManageCertificates() {
  const { isAuthenticated, primaryRole } = useContext(AuthContext);

  const [templates, setTemplates] = useState([]);
  const [institutes, setInstitutes] = useState([]);
  const [selectedInstituteFilter, setSelectedInstituteFilter] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);

  // Modal states
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeStudioTab, setActiveStudioTab] = useState("name"); // 'name' | 'qr' | 'preview'

  // Server Live Preview Modal state
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [previewImageBase64, setPreviewImageBase64] = useState(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [previewSampleName, setPreviewSampleName] =
    useState("MOHIT SHAHARWALE");

  // Issued Certificates Modal state
  const [isIssuedModalOpen, setIsIssuedModalOpen] = useState(false);
  const [currentTemplateForIssued, setCurrentTemplateForIssued] =
    useState(null);
  const [issuedCerts, setIssuedCerts] = useState([]);
  const [isIssuedLoading, setIsIssuedLoading] = useState(false);
  const [revokeReasonInput, setRevokeReasonInput] = useState("");
  const [certToRevoke, setCertToRevoke] = useState(null);
  const [isRevokeModalOpen, setIsRevokeModalOpen] = useState(false);
  const [isRevoking, setIsRevoking] = useState(false);

  // Form State for Template Creation / Editing
  const [formName, setFormName] = useState("");
  const [formInstituteId, setFormInstituteId] = useState(null);
  const [formIsActive, setFormIsActive] = useState(true);
  const [formImageFile, setFormImageFile] = useState(null);
  const [formImagePreviewUrl, setFormImagePreviewUrl] = useState("");
  const [imageNaturalSize, setImageNaturalSize] = useState({
    width: 1920,
    height: 1080,
  });

  // Name Bounding Box State (percentages 0-100)
  const [nameBox, setNameBox] = useState({
    x: 20,
    y: 50,
    width: 60,
    height: 12,
  });

  // Name Typography Style State
  const [nameStyle, setNameStyle] = useState({
    fontFamily: "Montserrat",
    fontSize: 48,
    fontWeight: "bold",
    fontStyle: "normal",
    color: "#1a1a2e",
    textAlign: "center",
    letterSpacing: 0,
    opacity: 1,
  });

  // QR Position & Colors State (percentages 0-100)
  const [qrBox, setQrBox] = useState({
    x: 82,
    y: 78,
    size: 10,
  });

  const [qrColors, setQrColors] = useState({
    darkColor: "#000000",
    lightColor: "#ffffff",
  });

  // Dynamic canvas display size for live font & element scaling
  const [canvasDisplaySize, setCanvasDisplaySize] = useState({
    width: 0,
    height: 0,
  });

  // Interactive Canvas Drag & Resize Refs and Active Snap Guides
  const previewCanvasRef = useRef(null);
  const dragStateRef = useRef(null);
  const [activeSnapGuides, setActiveSnapGuides] = useState({
    x: false,
    y: false,
  });

  const breadcrumbs = [
    { name: "Home", path: "/superadmin", isLast: false },
    {
      name: "Manage Certificates",
      path: "/superadmin/manage-certificates",
      isLast: true,
    },
  ];

  // Measure canvas element live to scale typography and elements accurately
  const updateCanvasDisplaySize = useCallback(() => {
    if (previewCanvasRef.current) {
      const rect = previewCanvasRef.current.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        setCanvasDisplaySize({ width: rect.width, height: rect.height });
      }
    }
  }, []);

  useEffect(() => {
    if (!isEditorOpen || !previewCanvasRef.current) return;
    updateCanvasDisplaySize();

    const observer = new ResizeObserver(() => {
      updateCanvasDisplaySize();
    });
    observer.observe(previewCanvasRef.current);
    return () => observer.disconnect();
  }, [updateCanvasDisplaySize, formImagePreviewUrl, isEditorOpen]);

  // Dynamically ensure Google Fonts are loaded into head
  useEffect(() => {
    const fontLinkId = "grademe-certificate-fonts-loader";
    if (!document.getElementById(fontLinkId)) {
      const link = document.createElement("link");
      link.id = fontLinkId;
      link.rel = "stylesheet";
      link.href =
        "https://fonts.googleapis.com/css2?family=Alex+Brush&family=Cinzel:wght@400;600;700;800&family=Dancing+Script:wght@400;600;700&family=Great+Vibes&family=Inter:wght@300;400;500;600;700;800&family=Merriweather:ital,wght@0,300;0,400;0,700;1,300;1,400&family=Montserrat:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,400;1,700&family=Outfit:wght@300;400;500;600;700;800&family=Parisienne&family=Pinyon+Script&family=Playfair+Display:ital,wght@0,400;0,600;0,700;0,800;1,400;1,700&family=Roboto:ital,wght@0,300;0,400;0,500;0,700;1,400&display=swap";
      document.head.appendChild(link);
    }
  }, []);

  // Fetch Institutes
  const fetchInstitutes = useCallback(async () => {
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/institute/get-all`,
        {
          credentials: "include",
        },
      );
      if (response && response.ok) {
        const data = await response.json();
        const list = Array.isArray(data) ? data : data.data || [];
        setInstitutes(
          list.map((inst) => ({
            value: String(inst._id),
            label: `${inst.name} (${inst.code || "N/A"})`,
            name: inst.name,
            code: inst.code,
          })),
        );
      }
    } catch (err) {
      console.error("Failed to fetch institutes:", err);
    }
  }, []);

  // Helper to reliably resolve institute display name
  const getInstituteName = useCallback(
    (instId) => {
      if (!instId) return "Global / Unassigned";
      if (typeof instId === "object" && instId.name) return instId.name;
      const targetId = typeof instId === "object" ? instId._id : instId;
      const found = institutes.find(
        (i) => String(i.value) === String(targetId),
      );
      return found ? found.name || found.label : "Assigned Institute";
    },
    [institutes],
  );

  // Fetch Templates
  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (selectedInstituteFilter?.value) {
        params.instituteId = selectedInstituteFilter.value;
      }
      const data = await certificateService.getAllCertificateTemplates(params);
      if (data && data.success) {
        setTemplates(data.data || []);
      }
    } catch (err) {
      console.error("Failed to fetch templates:", err);
      swalError(
        err?.response?.data?.message ||
          err.message ||
          "Failed to load certificate templates.",
      );
    } finally {
      setLoading(false);
    }
  }, [selectedInstituteFilter]);

  useEffect(() => {
    fetchInstitutes();
  }, [fetchInstitutes]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  // Open Create Form
  const handleOpenCreate = () => {
    setEditingTemplate(null);
    setFormName("");
    setFormInstituteId(institutes.length > 0 ? institutes[0].value : null);
    setFormIsActive(true);
    setFormImageFile(null);
    setFormImagePreviewUrl("");
    setImageNaturalSize({ width: 1920, height: 1080 });

    setNameBox({
      x: 20,
      y: 48,
      width: 60,
      height: 12,
    });

    setNameStyle({
      fontFamily: "Montserrat",
      fontSize: 48,
      fontWeight: "bold",
      fontStyle: "normal",
      color: "#1a1a2e",
      textAlign: "center",
      letterSpacing: 0,
      opacity: 1,
    });

    setQrBox({
      x: 82,
      y: 76,
      size: 10,
    });

    setQrColors({
      darkColor: "#000000",
      lightColor: "#ffffff",
    });

    setActiveStudioTab("name");
    setIsEditorOpen(true);
  };

  // Open Edit Form
  const handleOpenEdit = (template) => {
    if (!template) return;
    setEditingTemplate(template);
    setFormName(template.name || "");
    const instId =
      template.instituteId?._id ||
      (typeof template.instituteId === "string"
        ? template.instituteId
        : null) ||
      template.institute?._id ||
      (typeof template.institute === "string" ? template.institute : null) ||
      null;
    setFormInstituteId(instId ? String(instId) : null);
    setFormIsActive(template.isActive !== false);
    setFormImageFile(null);

    const fullUrl = getFullImageUrl(template.templateImageURL);
    setFormImagePreviewUrl(fullUrl);

    const naturalW = Number(template.imageWidth) || 1920;
    const naturalH = Number(template.imageHeight) || 1080;
    setImageNaturalSize({ width: naturalW, height: naturalH });

    // Load actual image dimensions from preview image
    if (fullUrl) {
      const img = new Image();
      img.onload = () => {
        if (img.naturalWidth && img.naturalHeight) {
          setImageNaturalSize({
            width: img.naturalWidth,
            height: img.naturalHeight,
          });
        }
      };
      img.src = fullUrl;
    }

    let parsedNameField = template.nameField;
    if (typeof parsedNameField === "string") {
      try {
        parsedNameField = JSON.parse(parsedNameField);
      } catch (e) {
        console.warn("Could not JSON.parse nameField", e);
      }
    }

    if (parsedNameField?.boundingBox) {
      setNameBox({
        x: Number(parsedNameField.boundingBox.x) ?? 20,
        y: Number(parsedNameField.boundingBox.y) ?? 48,
        width: Number(parsedNameField.boundingBox.width) ?? 60,
        height: Number(parsedNameField.boundingBox.height) ?? 12,
      });
    } else {
      setNameBox({ x: 20, y: 48, width: 60, height: 12 });
    }

    if (parsedNameField?.style) {
      setNameStyle({
        fontFamily: parsedNameField.style.fontFamily || "Montserrat",
        fontSize: Number(parsedNameField.style.fontSize) || 48,
        fontWeight: String(parsedNameField.style.fontWeight || "bold"),
        fontStyle: parsedNameField.style.fontStyle || "normal",
        color: parsedNameField.style.color || "#1a1a2e",
        textAlign: parsedNameField.style.textAlign || "center",
        letterSpacing: Number(parsedNameField.style.letterSpacing) || 0,
        opacity: Number(parsedNameField.style.opacity ?? 1),
      });
    } else {
      setNameStyle({
        fontFamily: "Montserrat",
        fontSize: 48,
        fontWeight: "bold",
        fontStyle: "normal",
        color: "#1a1a2e",
        textAlign: "center",
        letterSpacing: 0,
        opacity: 1,
      });
    }

    let parsedQrConfig = template.qrConfig;
    if (typeof parsedQrConfig === "string") {
      try {
        parsedQrConfig = JSON.parse(parsedQrConfig);
      } catch (e) {
        console.warn("Could not JSON.parse qrConfig", e);
      }
    }

    if (parsedQrConfig?.position) {
      setQrBox({
        x: Number(parsedQrConfig.position.x) ?? 82,
        y: Number(parsedQrConfig.position.y) ?? 76,
        size: Number(parsedQrConfig.position.size) ?? 10,
      });
    } else {
      setQrBox({ x: 82, y: 76, size: 10 });
    }

    if (parsedQrConfig) {
      setQrColors({
        darkColor: parsedQrConfig.darkColor || "#000000",
        lightColor: parsedQrConfig.lightColor || "#ffffff",
      });
    } else {
      setQrColors({ darkColor: "#000000", lightColor: "#ffffff" });
    }

    setActiveStudioTab("name");
    setIsEditorOpen(true);
  };

  // Handle Base Image Selection
  const handleImageFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      swalWarning("Please select a valid image file (PNG, JPG, JPEG).");
      return;
    }

    setFormImageFile(file);
    const objectUrl = URL.createObjectURL(file);
    setFormImagePreviewUrl(objectUrl);

    // Read natural dimensions
    const img = new Image();
    img.onload = () => {
      if (img.naturalWidth && img.naturalHeight) {
        setImageNaturalSize({
          width: img.naturalWidth,
          height: img.naturalHeight,
        });
      }
    };
    img.src = objectUrl;
  };

  // Delete Template
  const handleDeleteTemplate = async (template) => {
    const confirmed = await swalConfirm(
      "Delete Certificate Template?",
      `Are you sure you want to permanently delete "${template.name}"? This action cannot be undone.`,
      "Yes, Delete",
      "Cancel",
    );

    if (confirmed.isConfirmed) {
      try {
        await certificateService.deleteCertificateTemplate(template._id);
        swalSuccess("Certificate template deleted successfully.");
        fetchTemplates();
      } catch (err) {
        swalError(
          err?.response?.data?.message ||
            err.message ||
            "Failed to delete template.",
        );
      }
    }
  };

  // Save / Update Template
  const handleSaveTemplate = async (e) => {
    e.preventDefault();

    if (!formName.trim()) {
      swalWarning("Template name is required.");
      return;
    }

    if (!formInstituteId) {
      swalWarning("Please select an institute.");
      return;
    }

    if (!editingTemplate && !formImageFile) {
      swalWarning("Base certificate template image is required.");
      return;
    }

    try {
      setIsSubmitting(true);
      const formData = new FormData();
      formData.append("name", formName.trim());
      formData.append("instituteId", String(formInstituteId));
      formData.append("institute", String(formInstituteId));
      formData.append("isActive", String(formIsActive));
      formData.append("imageWidth", String(imageNaturalSize.width || 1920));
      formData.append("imageHeight", String(imageNaturalSize.height || 1080));

      const nameFieldData = {
        boundingBox: {
          x: Math.max(
            0,
            Math.min(100, parseFloat(Number(nameBox.x).toFixed(2))),
          ),
          y: Math.max(
            0,
            Math.min(100, parseFloat(Number(nameBox.y).toFixed(2))),
          ),
          width: Math.max(
            1,
            Math.min(100, parseFloat(Number(nameBox.width).toFixed(2))),
          ),
          height: Math.max(
            1,
            Math.min(100, parseFloat(Number(nameBox.height).toFixed(2))),
          ),
        },
        style: {
          ...nameStyle,
          fontSize: Number(nameStyle.fontSize) || 48,
          letterSpacing: Number(nameStyle.letterSpacing) || 0,
          opacity: Number(nameStyle.opacity) || 1,
        },
      };

      const qrConfigData = {
        position: {
          x: Math.max(0, Math.min(100, parseFloat(Number(qrBox.x).toFixed(2)))),
          y: Math.max(0, Math.min(100, parseFloat(Number(qrBox.y).toFixed(2)))),
          size: Math.max(
            1,
            Math.min(100, parseFloat(Number(qrBox.size).toFixed(2))),
          ),
        },
        darkColor: qrColors.darkColor || "#000000",
        lightColor: qrColors.lightColor || "#ffffff",
      };

      formData.append("nameField", JSON.stringify(nameFieldData));
      formData.append("qrConfig", JSON.stringify(qrConfigData));

      if (formImageFile) {
        formData.append("templateImage", formImageFile);
      }

      if (editingTemplate) {
        await certificateService.updateCertificateTemplate(
          editingTemplate._id,
          formData,
        );
        swalSuccess("Certificate template updated successfully!");
      } else {
        await certificateService.addCertificateTemplate(formData);
        swalSuccess("Certificate template created successfully!");
      }

      setIsEditorOpen(false);
      setEditingTemplate(null);
      await fetchTemplates();
    } catch (err) {
      console.error("Save template error:", err);
      swalError(
        err?.response?.data?.message ||
          err.message ||
          "Failed to save template.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Trigger Live Server Preview
  const handleTriggerServerPreview = async (templateIdToPreview) => {
    try {
      const targetId = templateIdToPreview || editingTemplate?._id;
      if (!targetId) {
        swalWarning("Please save the template first to generate an preview.");
        return;
      }

      setIsPreviewLoading(true);
      setIsPreviewModalOpen(true);
      setPreviewImageBase64(null);

      const response = await certificateService.previewCertificate({
        templateId: targetId,
        sampleName: previewSampleName || "MOHIT SHAHARWALE",
      });

      if (response && response.success && response.base64) {
        setPreviewImageBase64(response.base64);
      } else {
        throw new Error(response.message || "Failed to generate preview.");
      }
    } catch (err) {
      console.error("Preview error:", err);
      swalError(
        err?.response?.data?.message ||
          err.message ||
          "Failed to generate preview.",
      );
      setIsPreviewModalOpen(false);
    } finally {
      setIsPreviewLoading(false);
    }
  };

  // Open Issued Certificates for a Template
  const handleOpenIssuedCerts = async (template) => {
    setCurrentTemplateForIssued(template);
    setIsIssuedModalOpen(true);
    setIsIssuedLoading(true);
    setIssuedCerts([]);

    try {
      const response = await certificateService.getIssuedCertificates({
        templateId: template._id,
      });
      if (response && response.success) {
        setIssuedCerts(response.data || []);
      }
    } catch (err) {
      console.error("Failed to load issued certs:", err);
      swalError("Failed to load issued certificates.");
    } finally {
      setIsIssuedLoading(false);
    }
  };

  // Open Revoke Modal
  const handleOpenRevoke = (cert) => {
    setCertToRevoke(cert);
    setRevokeReasonInput("");
    setIsRevokeModalOpen(true);
  };

  // Execute Revocation
  const handleConfirmRevoke = async (e) => {
    e.preventDefault();
    if (!certToRevoke) return;

    try {
      setIsRevoking(true);
      await certificateService.revokeCertificate(certToRevoke._id, {
        reason: revokeReasonInput.trim() || "Revoked by Administrator",
      });

      swalSuccess("Certificate has been revoked.");
      setIsRevokeModalOpen(false);
      setCertToRevoke(null);

      // Refresh list
      if (currentTemplateForIssued) {
        handleOpenIssuedCerts(currentTemplateForIssued);
      }
    } catch (err) {
      swalError(
        err?.response?.data?.message ||
          err.message ||
          "Failed to revoke certificate.",
      );
    } finally {
      setIsRevoking(false);
    }
  };

  // ─────────────────────────────────────────────────────────────
  // Interactive Visual Canvas Drag & Resize Handlers
  // ─────────────────────────────────────────────────────────────

  const handleBoxMouseDown = (e, target, handleType = "move") => {
    e.preventDefault();
    e.stopPropagation();

    if (!previewCanvasRef.current) return;
    const canvasRect = previewCanvasRef.current.getBoundingClientRect();

    dragStateRef.current = {
      target, // 'name' | 'qr'
      handleType, // 'move', 'nw', 'ne', 'se', 'sw', 'n', 's', 'e', 'w', 'resize-qr'
      startX: e.clientX,
      startY: e.clientY,
      canvasRect,
      initialBox: target === "name" ? { ...nameBox } : { ...qrBox },
    };

    window.addEventListener("mousemove", handleBoxMouseMove);
    window.addEventListener("mouseup", handleBoxMouseUp);
  };

  const handleBoxMouseMove = useCallback((e) => {
    if (!dragStateRef.current) return;
    const { target, handleType, startX, startY, canvasRect, initialBox } =
      dragStateRef.current;

    const deltaXPercent = ((e.clientX - startX) / canvasRect.width) * 100;
    const deltaYPercent = ((e.clientY - startY) / canvasRect.height) * 100;
    const SNAP_TOLERANCE = 1.2; // Snap threshold in %
    let isSnappedX = false;
    let isSnappedY = false;

    if (target === "name") {
      let { x, y, width, height } = initialBox;

      if (handleType === "move") {
        let rawX = Math.max(
          0,
          Math.min(100 - width, initialBox.x + deltaXPercent),
        );
        let rawY = Math.max(
          0,
          Math.min(100 - height, initialBox.y + deltaYPercent),
        );

        // Magnetic snap to Horizontal Center (50%)
        const centerX = rawX + width / 2;
        if (Math.abs(centerX - 50) <= SNAP_TOLERANCE) {
          rawX = 50 - width / 2;
          isSnappedX = true;
        }

        // Magnetic snap to Vertical Center (50%)
        const centerY = rawY + height / 2;
        if (Math.abs(centerY - 50) <= SNAP_TOLERANCE) {
          rawY = 50 - height / 2;
          isSnappedY = true;
        }

        x = rawX;
        y = rawY;
      } else if (handleType === "se") {
        width = Math.max(
          5,
          Math.min(100 - x, initialBox.width + deltaXPercent),
        );
        height = Math.max(
          2,
          Math.min(100 - y, initialBox.height + deltaYPercent),
        );
      } else if (handleType === "sw") {
        const newX = Math.max(
          0,
          Math.min(
            initialBox.x + initialBox.width - 5,
            initialBox.x + deltaXPercent,
          ),
        );
        width = initialBox.width + (initialBox.x - newX);
        x = newX;
        height = Math.max(
          2,
          Math.min(100 - y, initialBox.height + deltaYPercent),
        );
      } else if (handleType === "ne") {
        const newY = Math.max(
          0,
          Math.min(
            initialBox.y + initialBox.height - 2,
            initialBox.y + deltaYPercent,
          ),
        );
        height = initialBox.height + (initialBox.y - newY);
        y = newY;
        width = Math.max(
          5,
          Math.min(100 - x, initialBox.width + deltaXPercent),
        );
      } else if (handleType === "nw") {
        const newX = Math.max(
          0,
          Math.min(
            initialBox.x + initialBox.width - 5,
            initialBox.x + deltaXPercent,
          ),
        );
        const newY = Math.max(
          0,
          Math.min(
            initialBox.y + initialBox.height - 2,
            initialBox.y + deltaYPercent,
          ),
        );
        width = initialBox.width + (initialBox.x - newX);
        height = initialBox.height + (initialBox.y - newY);
        x = newX;
        y = newY;
      } else if (handleType === "e") {
        let newWidth = Math.max(
          5,
          Math.min(100 - x, initialBox.width + deltaXPercent),
        );
        const centerX = x + newWidth / 2;
        if (Math.abs(centerX - 50) <= SNAP_TOLERANCE) {
          newWidth = (50 - x) * 2;
          isSnappedX = true;
        }
        width = newWidth;
      } else if (handleType === "w") {
        let newX = Math.max(
          0,
          Math.min(
            initialBox.x + initialBox.width - 5,
            initialBox.x + deltaXPercent,
          ),
        );
        let newWidth = initialBox.width + (initialBox.x - newX);
        const centerX = newX + newWidth / 2;
        if (Math.abs(centerX - 50) <= SNAP_TOLERANCE) {
          newX = 50 - newWidth / 2;
          isSnappedX = true;
        }
        width = newWidth;
        x = newX;
      } else if (handleType === "s") {
        height = Math.max(
          2,
          Math.min(100 - y, initialBox.height + deltaYPercent),
        );
      } else if (handleType === "n") {
        const newY = Math.max(
          0,
          Math.min(
            initialBox.y + initialBox.height - 2,
            initialBox.y + deltaYPercent,
          ),
        );
        height = initialBox.height + (initialBox.y - newY);
        y = newY;
      }

      setNameBox({
        x: parseFloat(x.toFixed(2)),
        y: parseFloat(y.toFixed(2)),
        width: parseFloat(width.toFixed(2)),
        height: parseFloat(height.toFixed(2)),
      });
    } else if (target === "qr") {
      let { x, y, size } = initialBox;

      if (handleType === "move") {
        let rawX = Math.max(
          0,
          Math.min(100 - size, initialBox.x + deltaXPercent),
        );
        const aspect = canvasRect.width / canvasRect.height;
        const heightPercent = size * aspect;
        let rawY = Math.max(
          0,
          Math.min(100 - heightPercent, initialBox.y + deltaYPercent),
        );

        // Magnetic snap to Horizontal Center (50%)
        const qrCenterX = rawX + size / 2;
        if (Math.abs(qrCenterX - 50) <= SNAP_TOLERANCE) {
          rawX = 50 - size / 2;
          isSnappedX = true;
        }

        // Magnetic snap to Vertical Center (50%)
        const qrCenterY = rawY + heightPercent / 2;
        if (Math.abs(qrCenterY - 50) <= SNAP_TOLERANCE) {
          rawY = 50 - heightPercent / 2;
          isSnappedY = true;
        }

        x = rawX;
        y = rawY;
      } else if (handleType === "resize-qr" || handleType === "se") {
        size = Math.max(3, Math.min(100 - x, initialBox.size + deltaXPercent));
      }

      setQrBox({
        x: parseFloat(x.toFixed(2)),
        y: parseFloat(y.toFixed(2)),
        size: parseFloat(size.toFixed(2)),
      });
    }

    setActiveSnapGuides({ x: isSnappedX, y: isSnappedY });
  }, []);

  const handleBoxMouseUp = useCallback(() => {
    dragStateRef.current = null;
    setActiveSnapGuides({ x: false, y: false });
    window.removeEventListener("mousemove", handleBoxMouseMove);
    window.removeEventListener("mouseup", handleBoxMouseUp);
  }, [handleBoxMouseMove]);

  const nameCenterX = parseFloat(
    (
      (Number(nameBox.x) || 0) +
      (Number(nameBox.width) || 0) / 2
    ).toFixed(1),
  );
  const isNameCenteredX = Math.abs(nameCenterX - 50) <= 0.3;

  const qrCenterX = parseFloat(
    (
      (Number(qrBox.x) || 0) +
      (Number(qrBox.size) || 0) / 2
    ).toFixed(1),
  );
  const isQrCenteredX = Math.abs(qrCenterX - 50) <= 0.3;

  // Filter templates
  const filteredTemplates = templates.filter((tpl) => {
    const instName = getInstituteName(tpl.instituteId);
    const matchesSearch =
      tpl.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      instName.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  if (!isAuthenticated || primaryRole !== "superadmin") {
    return <Navigate to="/login" replace />;
  }

  return (
    <div
      className="super-admin-container"
      style={{ background: "#f8fafc", minHeight: "100vh" }}
    >
      <Navbar
        pageName="GradeMe-AI Certificate Templates"
        breadcrumbs={breadcrumbs}
      />

      <div
        style={{ maxWidth: "1400px", margin: "0 auto", padding: "2rem 1.5rem" }}
      >
        {/* Top Header & Actions */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "1rem",
            marginBottom: "2rem",
            background: "#ffffff",
            padding: "1.5rem",
            borderRadius: "12px",
            border: "1px solid #e2e8f0",
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
          }}
        >
          <div>
            <h1
              style={{
                margin: 0,
                fontSize: "1.6rem",
                fontWeight: "700",
                color: "#0f172a",
              }}
            >
              Certificate Templates
            </h1>
            <p
              style={{
                margin: "4px 0 0 0",
                color: "#64748b",
                fontSize: "0.9rem",
              }}
            >
              Design base templates with interactive Name and QR placement for
              automated certificate issuance.
            </p>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.75rem",
              flexWrap: "wrap",
            }}
          >
            {/* Institute Filter */}
            <div style={{ width: "240px" }}>
              <Select
                options={[
                  { value: "", label: "All Institutes" },
                  ...institutes,
                ]}
                value={selectedInstituteFilter}
                onChange={setSelectedInstituteFilter}
                placeholder="Filter by Institute..."
                styles={selectCustomStyles}
                isClearable
              />
            </div>

            {/* Search Input */}
            <div style={{ position: "relative", minWidth: "220px" }}>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search templates..."
                style={{
                  width: "100%",
                  padding: "9px 12px 9px 36px",
                  borderRadius: "8px",
                  border: "1px solid #d1d5db",
                  fontSize: "0.9rem",
                  outline: "none",
                  backgroundColor: "#ffffff",
                }}
              />
              <i
                className="fa-solid fa-magnifying-glass"
                style={{
                  position: "absolute",
                  left: "12px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#94a3b8",
                  fontSize: "0.85rem",
                }}
              />
            </div>

            {/* Create CTA Button */}
            <button
              onClick={handleOpenCreate}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                padding: "10px 18px",
                borderRadius: "8px",
                backgroundColor: "var(--color-1, #f09a21)",
                color: "#ffffff",
                border: "none",
                fontWeight: "600",
                fontSize: "0.92rem",
                cursor: "pointer",
                boxShadow: "0 2px 6px rgba(240, 154, 33, 0.3)",
                transition: "background 0.2s",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.9")}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
            >
              <i className="fa-solid fa-plus" />
              <span>Create Template</span>
            </button>
          </div>
        </div>

        {/* Template Grid */}
        {loading ? (
          <div style={{ textAlign: "center", padding: "4rem" }}>
            <Loader style={{ fontSize: "40px", left: "0" }} />
          </div>
        ) : filteredTemplates.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              padding: "4rem 2rem",
              background: "#ffffff",
              borderRadius: "12px",
              border: "2px dashed #e2e8f0",
            }}
          >
            <div
              style={{
                fontSize: "3rem",
                color: "#cbd5e1",
                marginBottom: "1rem",
              }}
            >
              <i className="fa-solid fa-award" />
            </div>
            <h3
              style={{
                margin: "0 0 0.5rem 0",
                color: "#334155",
                fontSize: "1.2rem",
              }}
            >
              No Certificate Templates Found
            </h3>
            <p
              style={{
                color: "#64748b",
                maxWidth: "450px",
                margin: "0 auto 1.5rem auto",
                fontSize: "0.9rem",
              }}
            >
              Upload your first base certificate image and set up bounding boxes
              for student name and QR code.
            </p>
            <button
              onClick={handleOpenCreate}
              style={{
                padding: "10px 20px",
                borderRadius: "8px",
                backgroundColor: "var(--color-1, #f09a21)",
                color: "#ffffff",
                border: "none",
                fontWeight: "600",
                fontSize: "0.9rem",
                cursor: "pointer",
              }}
            >
              Create New Template
            </button>
          </div>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
              gap: "1.5rem",
            }}
          >
            {filteredTemplates.map((template) => (
              <div
                key={template._id}
                style={{
                  background: "#ffffff",
                  borderRadius: "12px",
                  border: "1px solid #e2e8f0",
                  overflow: "hidden",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.04)",
                  display: "flex",
                  flexDirection: "column",
                  transition: "transform 0.2s, box-shadow 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-3px)";
                  e.currentTarget.style.boxShadow =
                    "0 8px 20px rgba(0,0,0,0.08)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow =
                    "0 2px 6px rgba(0,0,0,0.04)";
                }}
              >
                {/* Thumbnail Header */}
                <div
                  style={{
                    position: "relative",
                    width: "100%",
                    paddingTop: "62%",
                    backgroundColor: "#f1f5f9",
                    overflow: "hidden",
                    borderBottom: "1px solid #e2e8f0",
                  }}
                >
                  <img
                    src={getFullImageUrl(template.templateImageURL)}
                    alt={template.name}
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                  {/* Status Badge */}
                  <span
                    style={{
                      position: "absolute",
                      top: "10px",
                      right: "10px",
                      padding: "4px 10px",
                      borderRadius: "20px",
                      fontSize: "0.75rem",
                      fontWeight: "700",
                      backgroundColor:
                        template.isActive !== false ? "#dcfce7" : "#f1f5f9",
                      color:
                        template.isActive !== false ? "#15803d" : "#64748b",
                      border: `1px solid ${template.isActive !== false ? "#86efac" : "#cbd5e1"}`,
                    }}
                  >
                    {template.isActive !== false ? "Active" : "Inactive"}
                  </span>
                </div>

                {/* Card Body */}
                <div
                  style={{
                    padding: "1.25rem",
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <h3
                    style={{
                      margin: "0 0 0.4rem 0",
                      fontSize: "1.1rem",
                      fontWeight: "700",
                      color: "#0f172a",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={template.name}
                  >
                    {template.name}
                  </h3>

                  <p
                    style={{
                      margin: "0 0 0.8rem 0",
                      fontSize: "0.85rem",
                      color: "#64748b",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    <i
                      className="fa-solid fa-school"
                      style={{ color: "var(--color-1, #f09a21)" }}
                    />
                    <span style={{ fontWeight: "600" }}>
                      {getInstituteName(template.instituteId)}
                    </span>
                  </p>

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      fontSize: "0.78rem",
                      color: "#94a3b8",
                      marginBottom: "1rem",
                      borderTop: "1px solid #f1f5f9",
                      paddingTop: "0.75rem",
                    }}
                  >
                    <span>
                      Dimensions: {template.imageWidth || "1920"}x
                      {template.imageHeight || "1080"}px
                    </span>
                    <span>
                      {new Date(
                        template.createdAt || Date.now(),
                      ).toLocaleDateString()}
                    </span>
                  </div>

                  {/* Action Buttons */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "0.5rem",
                      marginTop: "auto",
                    }}
                  >
                    <button
                      onClick={() => handleOpenEdit(template)}
                      style={{
                        padding: "8px 12px",
                        borderRadius: "6px",
                        border: "1px solid #cbd5e1",
                        background: "#ffffff",
                        color: "#334155",
                        fontSize: "0.82rem",
                        fontWeight: "600",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "6px",
                        transition: "all 0.2s",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor =
                          "var(--color-1, #f09a21)";
                        e.currentTarget.style.color = "var(--color-1, #f09a21)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = "#cbd5e1";
                        e.currentTarget.style.color = "#334155";
                      }}
                    >
                      <i className="fa-solid fa-pen-to-square" />
                      <span>Edit</span>
                    </button>

                    <button
                      onClick={() => handleTriggerServerPreview(template._id)}
                      style={{
                        padding: "8px 12px",
                        borderRadius: "6px",
                        border: "1px solid #cbd5e1",
                        background: "#ffffff",
                        color: "#334155",
                        fontSize: "0.82rem",
                        fontWeight: "600",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "6px",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = "#3b82f6";
                        e.currentTarget.style.color = "#3b82f6";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = "#cbd5e1";
                        e.currentTarget.style.color = "#334155";
                      }}
                    >
                      <i className="fa-solid fa-eye" />
                      <span>Preview</span>
                    </button>

                    <button
                      onClick={() => handleOpenIssuedCerts(template)}
                      style={{
                        padding: "8px 12px",
                        borderRadius: "6px",
                        border: "1px solid #cbd5e1",
                        background: "#ffffff",
                        color: "#334155",
                        fontSize: "0.82rem",
                        fontWeight: "600",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "6px",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor =
                          "var(--color-2, #da1c5c)";
                        e.currentTarget.style.color = "var(--color-2, #da1c5c)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = "#cbd5e1";
                        e.currentTarget.style.color = "#334155";
                      }}
                    >
                      <i className="fa-solid fa-list-check" />
                      <span>Issued</span>
                    </button>

                    <button
                      onClick={() => handleDeleteTemplate(template)}
                      style={{
                        padding: "8px 12px",
                        borderRadius: "6px",
                        border: "1px solid #fee2e2",
                        background: "#ffffff",
                        color: "#ef4444",
                        fontSize: "0.82rem",
                        fontWeight: "600",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "6px",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#fee2e2";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "#ffffff";
                      }}
                    >
                      <i className="fa-solid fa-trash" />
                      <span>Delete</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ─────────────────────────────────────────────────────────────
          CREATE / EDIT STUDIO MODAL
      ───────────────────────────────────────────────────────────── */}
      {isEditorOpen && (
        <Modal
          isOpen={isEditorOpen}
          onClose={() => !isSubmitting && setIsEditorOpen(false)}
          maxWidth="1350px"
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "1.25rem",
              maxHeight: "88vh",
            }}
          >
            {/* Modal Header */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                borderBottom: "1px solid #e2e8f0",
                paddingBottom: "0.75rem",
              }}
            >
              <div>
                <h2
                  style={{
                    margin: 0,
                    fontSize: "1.4rem",
                    fontWeight: "700",
                    color: "#0f172a",
                  }}
                >
                  {editingTemplate
                    ? "Edit Certificate Template"
                    : "Create Certificate Template"}
                </h2>
                <p
                  style={{
                    margin: "2px 0 0 0",
                    color: "#64748b",
                    fontSize: "0.85rem",
                  }}
                >
                  Upload a background certificate template image and position
                  the student name and QR code.
                </p>
              </div>

              <div style={{ display: "flex", gap: "0.75rem" }}>
                <button
                  type="button"
                  onClick={handleSaveTemplate}
                  disabled={isSubmitting}
                  style={{
                    padding: "8px 18px",
                    borderRadius: "6px",
                    border: "none",
                    backgroundColor: "var(--color-1, #f09a21)",
                    color: "#ffffff",
                    fontSize: "0.88rem",
                    fontWeight: "600",
                    cursor: isSubmitting ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  {isSubmitting ? (
                    <i className="fa-solid fa-spinner fa-spin" />
                  ) : (
                    <i className="fa-solid fa-floppy-disk" />
                  )}
                  <span>
                    {editingTemplate ? "Update Template" : "Save Template"}
                  </span>
                </button>
              </div>
            </div>

            {/* Studio Workspace Layout */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 380px",
                gap: "1.5rem",
                overflowY: "auto",
                paddingRight: "4px",
              }}
            >
              {/* Left Column: Visual Placement Canvas */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "1rem",
                  background: "#f8fafc",
                  padding: "1rem",
                  borderRadius: "10px",
                  border: "1px solid #e2e8f0",
                }}
              >
                {/* Visual Canvas Information Bar */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    fontSize: "0.82rem",
                    color: "#475569",
                  }}
                >
                  <span>
                    <i
                      className="fa-solid fa-arrows-up-down-left-right"
                      style={{
                        marginRight: "6px",
                        color: "var(--color-1, #f09a21)",
                      }}
                    />
                    Drag and resize the boxes directly on the canvas.
                  </span>
                  <span style={{ fontWeight: "600", color: "#0f172a" }}>
                    Canvas: {imageNaturalSize.width} × {imageNaturalSize.height}{" "}
                    px
                  </span>
                </div>

                {/* The Interactive Visual Canvas */}
                {formImagePreviewUrl ? (
                  <div
                    ref={previewCanvasRef}
                    style={{
                      position: "relative",
                      width: "100%",
                      backgroundColor: "#ffffff",
                      borderRadius: "8px",
                      overflow: "hidden",
                      border: "1px solid #cbd5e1",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
                      userSelect: "none",
                    }}
                  >
                    {/* Base Image */}
                    <img
                      src={getFullImageUrl(formImagePreviewUrl)}
                      alt="Template Base"
                      style={{
                        width: "100%",
                        height: "auto",
                        display: "block",
                        pointerEvents: "none",
                      }}
                    />

                    {/* Center Alignment Guidelines (Displayed ONLY while holding and moving) */}
                    {activeSnapGuides.x && (
                      <div
                        style={{
                          position: "absolute",
                          top: 0,
                          bottom: 0,
                          left: "50%",
                          width: "2px",
                          backgroundColor: "#ec4899",
                          boxShadow:
                            "0 0 8px rgba(236, 72, 153, 0.9), 0 0 2px rgba(236, 72, 153, 1)",
                          zIndex: 25,
                          pointerEvents: "none",
                          transform: "translateX(-50%)",
                        }}
                      />
                    )}

                    {activeSnapGuides.y && (
                      <div
                        style={{
                          position: "absolute",
                          left: 0,
                          right: 0,
                          top: "50%",
                          height: "2px",
                          backgroundColor: "#ec4899",
                          boxShadow:
                            "0 0 8px rgba(236, 72, 153, 0.9), 0 0 2px rgba(236, 72, 153, 1)",
                          zIndex: 25,
                          pointerEvents: "none",
                          transform: "translateY(-50%)",
                        }}
                      />
                    )}

                    {activeSnapGuides.x && activeSnapGuides.y && (
                      <div
                        style={{
                          position: "absolute",
                          left: "50%",
                          top: "50%",
                          transform: "translate(-50%, -50%)",
                          width: "14px",
                          height: "14px",
                          borderRadius: "50%",
                          border: "2px solid #ec4899",
                          backgroundColor: "#ffffff",
                          boxShadow: "0 0 12px rgba(236, 72, 153, 1)",
                          zIndex: 26,
                          pointerEvents: "none",
                        }}
                      />
                    )}

                    {/* ─────────────────────────────────────────────────────────────
                        INTERACTIVE NAME BOUNDING BOX
                    ───────────────────────────────────────────────────────────── */}
                    <div
                      onMouseDown={(e) => handleBoxMouseDown(e, "name", "move")}
                      style={{
                        position: "absolute",
                        left: `${nameBox.x}%`,
                        top: `${nameBox.y}%`,
                        width: `${nameBox.width}%`,
                        height: `${nameBox.height}%`,
                        border: isNameCenteredX
                          ? "2px solid #ec4899"
                          : "2px dashed #2563eb",
                        backgroundColor: isNameCenteredX
                          ? "rgba(236, 72, 153, 0.09)"
                          : "rgba(37, 99, 235, 0.08)",
                        cursor: "move",
                        display: "flex",
                        alignItems: "center",
                        justifyContent:
                          nameStyle.textAlign === "left"
                            ? "flex-start"
                            : nameStyle.textAlign === "right"
                              ? "flex-end"
                              : "center",
                        padding: "0 6px",
                        boxSizing: "border-box",
                        zIndex: 10,
                        transition:
                          "border-color 0.15s ease, background-color 0.15s ease",
                      }}
                      title="Student Name Field (Drag to move, use handles to resize)"
                    >
                      {/* Name Badge with Live Center Feedback */}
                      <div
                        style={{
                          position: "absolute",
                          top: "-24px",
                          left: "0",
                          backgroundColor: isNameCenteredX
                            ? "#ec4899"
                            : "#2563eb",
                          color: "#ffffff",
                          fontSize: "10px",
                          fontWeight: "700",
                          padding: "2px 8px",
                          borderRadius: "4px 4px 0 0",
                          pointerEvents: "none",
                          whiteSpace: "nowrap",
                          display: "flex",
                          alignItems: "center",
                          gap: "5px",
                          boxShadow: isNameCenteredX
                            ? "0 2px 8px rgba(236, 72, 153, 0.4)"
                            : "none",
                          transition: "background-color 0.15s ease",
                        }}
                      >
                        <span>NAME FIELD</span>
                        {isNameCenteredX ? (
                          <span
                            style={{
                              backgroundColor: "rgba(255,255,255,0.25)",
                              padding: "1px 5px",
                              borderRadius: "3px",
                              fontSize: "9px",
                              fontWeight: "800",
                            }}
                          >
                            🎯 CENTERED (50%)
                          </span>
                        ) : (
                          <span
                            style={{
                              opacity: 0.85,
                              fontSize: "9px",
                              fontWeight: "500",
                            }}
                          >
                            ({nameBox.width.toFixed(1)}% ×{" "}
                            {nameBox.height.toFixed(1)}% • Center: {nameCenterX}
                            %)
                          </span>
                        )}
                      </div>

                      {/* Center Alignment Notch Ticks on Box */}
                      <div
                        style={{
                          position: "absolute",
                          top: "-4px",
                          left: "50%",
                          transform: "translateX(-50%)",
                          width: "6px",
                          height: "6px",
                          borderRadius: "50%",
                          backgroundColor: isNameCenteredX
                            ? "#ec4899"
                            : "#2563eb",
                          border: "1px solid #ffffff",
                          pointerEvents: "none",
                        }}
                      />
                      <div
                        style={{
                          position: "absolute",
                          bottom: "-4px",
                          left: "50%",
                          transform: "translateX(-50%)",
                          width: "6px",
                          height: "6px",
                          borderRadius: "50%",
                          backgroundColor: isNameCenteredX
                            ? "#ec4899"
                            : "#2563eb",
                          border: "1px solid #ffffff",
                          pointerEvents: "none",
                        }}
                      />

                      {/* Live Text Preview in Box with exact scaled typography */}
                      {(() => {
                        const effectiveImageWidth =
                          imageNaturalSize.width || 1920;
                        const scaleRatio =
                          canvasDisplaySize.width > 0 && effectiveImageWidth > 0
                            ? canvasDisplaySize.width / effectiveImageWidth
                            : 0.45;
                        const previewFontSize = Math.max(
                          8,
                          Math.round(nameStyle.fontSize * scaleRatio),
                        );
                        const previewLetterSpacing =
                          (Number(nameStyle.letterSpacing) || 0) * scaleRatio;

                        return (
                          <span
                            style={{
                              fontFamily: getFontFamilyCss(
                                nameStyle.fontFamily,
                              ),
                              fontSize: `${previewFontSize}px`,
                              lineHeight: 1.15,
                              fontWeight: nameStyle.fontWeight,
                              fontStyle: nameStyle.fontStyle,
                              color: nameStyle.color,
                              letterSpacing: `${previewLetterSpacing}px`,
                              opacity: nameStyle.opacity,
                              textAlign: nameStyle.textAlign,
                              width: "100%",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              pointerEvents: "none",
                              display: "block",
                            }}
                          >
                            {previewSampleName || "STUDENT FULL NAME"}
                          </span>
                        );
                      })()}

                      {/* Resize Handles (NW, NE, SE, SW, E, W, N, S) */}
                      <div
                        onMouseDown={(e) => handleBoxMouseDown(e, "name", "nw")}
                        style={{
                          position: "absolute",
                          top: "-5px",
                          left: "-5px",
                          width: "10px",
                          height: "10px",
                          backgroundColor: isNameCenteredX
                            ? "#ec4899"
                            : "#2563eb",
                          border: "1px solid #ffffff",
                          cursor: "nw-resize",
                        }}
                      />
                      <div
                        onMouseDown={(e) => handleBoxMouseDown(e, "name", "ne")}
                        style={{
                          position: "absolute",
                          top: "-5px",
                          right: "-5px",
                          width: "10px",
                          height: "10px",
                          backgroundColor: isNameCenteredX
                            ? "#ec4899"
                            : "#2563eb",
                          border: "1px solid #ffffff",
                          cursor: "ne-resize",
                        }}
                      />
                      <div
                        onMouseDown={(e) => handleBoxMouseDown(e, "name", "se")}
                        style={{
                          position: "absolute",
                          bottom: "-5px",
                          right: "-5px",
                          width: "10px",
                          height: "10px",
                          backgroundColor: isNameCenteredX
                            ? "#ec4899"
                            : "#2563eb",
                          border: "1px solid #ffffff",
                          cursor: "se-resize",
                        }}
                      />
                      <div
                        onMouseDown={(e) => handleBoxMouseDown(e, "name", "sw")}
                        style={{
                          position: "absolute",
                          bottom: "-5px",
                          left: "-5px",
                          width: "10px",
                          height: "10px",
                          backgroundColor: isNameCenteredX
                            ? "#ec4899"
                            : "#2563eb",
                          border: "1px solid #ffffff",
                          cursor: "sw-resize",
                        }}
                      />
                    </div>

                    {/* ─────────────────────────────────────────────────────────────
                        INTERACTIVE QR BOUNDING BOX
                    ───────────────────────────────────────────────────────────── */}
                    <div
                      onMouseDown={(e) => handleBoxMouseDown(e, "qr", "move")}
                      style={{
                        position: "absolute",
                        left: `${qrBox.x}%`,
                        top: `${qrBox.y}%`,
                        width: `${qrBox.size}%`,
                        aspectRatio: "1 / 1",
                        border: isQrCenteredX
                          ? "2px solid #ec4899"
                          : "2px dashed #9333ea",
                        backgroundColor: isQrCenteredX
                          ? "rgba(236, 72, 153, 0.09)"
                          : "rgba(147, 51, 234, 0.12)",
                        cursor: "move",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        boxSizing: "border-box",
                        zIndex: 10,
                        transition:
                          "border-color 0.15s ease, background-color 0.15s ease",
                      }}
                      title="Verification QR Code (Drag to move, drag corner handle to resize)"
                    >
                      {/* QR Badge with Live Center Feedback */}
                      <div
                        style={{
                          position: "absolute",
                          top: "-24px",
                          left: "0",
                          backgroundColor: isQrCenteredX
                            ? "#ec4899"
                            : "#9333ea",
                          color: "#ffffff",
                          fontSize: "10px",
                          fontWeight: "700",
                          padding: "2px 8px",
                          borderRadius: "4px 4px 0 0",
                          pointerEvents: "none",
                          whiteSpace: "nowrap",
                          display: "flex",
                          alignItems: "center",
                          gap: "5px",
                          boxShadow: isQrCenteredX
                            ? "0 2px 8px rgba(236, 72, 153, 0.4)"
                            : "none",
                          transition: "background-color 0.15s ease",
                        }}
                      >
                        <span>QR CODE</span>
                        {isQrCenteredX ? (
                          <span
                            style={{
                              backgroundColor: "rgba(255,255,255,0.25)",
                              padding: "1px 5px",
                              borderRadius: "3px",
                              fontSize: "9px",
                              fontWeight: "800",
                            }}
                          >
                            🎯 CENTERED (50%)
                          </span>
                        ) : (
                          <span
                            style={{
                              opacity: 0.85,
                              fontSize: "9px",
                              fontWeight: "500",
                            }}
                          >
                            ({qrBox.size.toFixed(1)}% • Center: {qrCenterX}%)
                          </span>
                        )}
                      </div>

                      {/* Center Alignment Notch Ticks on QR Box */}
                      <div
                        style={{
                          position: "absolute",
                          top: "-4px",
                          left: "50%",
                          transform: "translateX(-50%)",
                          width: "6px",
                          height: "6px",
                          borderRadius: "50%",
                          backgroundColor: isQrCenteredX
                            ? "#ec4899"
                            : "#9333ea",
                          border: "1px solid #ffffff",
                          pointerEvents: "none",
                        }}
                      />
                      <div
                        style={{
                          position: "absolute",
                          bottom: "-4px",
                          left: "50%",
                          transform: "translateX(-50%)",
                          width: "6px",
                          height: "6px",
                          borderRadius: "50%",
                          backgroundColor: isQrCenteredX
                            ? "#ec4899"
                            : "#9333ea",
                          border: "1px solid #ffffff",
                          pointerEvents: "none",
                        }}
                      />

                      {/* Mock QR Representation */}
                      <div
                        style={{
                          width: "80%",
                          height: "80%",
                          backgroundColor: qrColors.lightColor || "#ffffff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          border: `1px solid ${qrColors.darkColor || "#000000"}`,
                          pointerEvents: "none",
                        }}
                      >
                        <i
                          className="fa-solid fa-qrcode"
                          style={{
                            fontSize: "clamp(16px, 2.5vw, 48px)",
                            color: qrColors.darkColor || "#000000",
                          }}
                        />
                      </div>

                      {/* QR Resize Handle */}
                      <div
                        onMouseDown={(e) =>
                          handleBoxMouseDown(e, "qr", "resize-qr")
                        }
                        style={{
                          position: "absolute",
                          bottom: "-6px",
                          right: "-6px",
                          width: "12px",
                          height: "12px",
                          backgroundColor: isQrCenteredX
                            ? "#ec4899"
                            : "#9333ea",
                          border: "1px solid #ffffff",
                          cursor: "se-resize",
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  <div
                    style={{
                      height: "360px",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "2px dashed #cbd5e1",
                      borderRadius: "8px",
                      backgroundColor: "#ffffff",
                    }}
                  >
                    <i
                      className="fa-regular fa-image"
                      style={{
                        fontSize: "3rem",
                        color: "#cbd5e1",
                        marginBottom: "1rem",
                      }}
                    />
                    <p
                      style={{ margin: 0, fontWeight: "600", color: "#475569" }}
                    >
                      Upload a base certificate image to start designing
                    </p>
                    <p
                      style={{
                        margin: "4px 0 1rem 0",
                        fontSize: "0.82rem",
                        color: "#94a3b8",
                      }}
                    >
                      Recommended: High resolution PNG or JPG (e.g. 1920x1080 or
                      A4 landscape)
                    </p>
                    <label
                      style={{
                        padding: "8px 16px",
                        borderRadius: "6px",
                        backgroundColor: "var(--color-1, #f09a21)",
                        color: "#ffffff",
                        fontWeight: "600",
                        fontSize: "0.85rem",
                        cursor: "pointer",
                      }}
                    >
                      <i
                        className="fa-solid fa-cloud-arrow-up"
                        style={{ marginRight: "6px" }}
                      />
                      Select Base Image
                      <input
                        type="file"
                        accept="image/png, image/jpeg, image/jpg"
                        onChange={handleImageFileChange}
                        style={{ display: "none" }}
                      />
                    </label>
                  </div>
                )}
              </div>

              {/* Right Column: Controls & Configuration Panels */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "1rem",
                  background: "#ffffff",
                  padding: "1rem",
                  borderRadius: "10px",
                  border: "1px solid #e2e8f0",
                }}
              >
                {/* Basic Meta Info */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.75rem",
                  }}
                >
                  <div>
                    <label
                      style={{
                        display: "block",
                        fontSize: "0.85rem",
                        fontWeight: "600",
                        color: "#334155",
                        marginBottom: "4px",
                      }}
                    >
                      Template Name *
                    </label>
                    <input
                      type="text"
                      value={formName}
                      onChange={(e) => setFormName(e.target.value)}
                      placeholder="e.g., Certificate of Excellence"
                      required
                      style={{
                        width: "100%",
                        padding: "8px 12px",
                        borderRadius: "6px",
                        border: "1px solid #d1d5db",
                        fontSize: "0.9rem",
                        outline: "none",
                      }}
                    />
                  </div>

                  <div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginBottom: "4px",
                      }}
                    >
                      <label
                        style={{
                          fontSize: "0.85rem",
                          fontWeight: "600",
                          color: "#334155",
                        }}
                      >
                        Institute *
                      </label>
                    </div>
                    <Select
                      options={institutes}
                      value={
                        institutes.find(
                          (i) => String(i.value) === String(formInstituteId),
                        ) || null
                      }
                      onChange={(opt) => setFormInstituteId(opt?.value || null)}
                      isDisabled={Boolean(editingTemplate)}
                      placeholder="Select Institute..."
                      styles={{
                        ...selectCustomStyles,
                        control: (base, state) => ({
                          ...selectCustomStyles.control(base, state),
                          backgroundColor: editingTemplate
                            ? "#f8fafc"
                            : "#ffffff",
                          cursor: editingTemplate ? "not-allowed" : "default",
                          opacity: editingTemplate ? 0.85 : 1,
                        }),
                      }}
                    />
                    {editingTemplate && (
                      <small
                        style={{
                          color: "#94a3b8",
                          fontSize: "0.75rem",
                          display: "block",
                          marginTop: "3px",
                        }}
                      >
                        Once created, certificate templates cannot be
                        transferred to another institute.
                      </small>
                    )}
                  </div>

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div>
                      <label
                        style={{
                          display: "block",
                          fontSize: "0.85rem",
                          fontWeight: "600",
                          color: "#334155",
                        }}
                      >
                        Replace / Change Image
                      </label>
                      <input
                        type="file"
                        accept="image/png, image/jpeg, image/jpg"
                        onChange={handleImageFileChange}
                        style={{
                          fontSize: "0.8rem",
                          color: "#64748b",
                          marginTop: "4px",
                        }}
                      />
                    </div>

                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        cursor: "pointer",
                        fontSize: "0.85rem",
                        fontWeight: "600",
                        color: "#334155",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={formIsActive}
                        onChange={(e) => setFormIsActive(e.target.checked)}
                        style={{
                          width: "16px",
                          height: "16px",
                          cursor: "pointer",
                        }}
                      />
                      <span>Active</span>
                    </label>
                  </div>
                </div>

                <hr
                  style={{
                    border: "none",
                    borderTop: "1px solid #e2e8f0",
                    margin: "0.5rem 0",
                  }}
                />

                {/* Studio Tabs for Detailed Placement */}
                <div
                  style={{ display: "flex", borderBottom: "1px solid #e2e8f0" }}
                >
                  {[
                    { id: "name", label: "Name Field", icon: "fa-font" },
                    { id: "qr", label: "QR Code", icon: "fa-qrcode" },
                    { id: "preview", label: "Sample Text", icon: "fa-sliders" },
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => setActiveStudioTab(tab.id)}
                      style={{
                        flex: 1,
                        padding: "8px 4px",
                        border: "none",
                        background: "none",
                        fontSize: "0.85rem",
                        fontWeight: activeStudioTab === tab.id ? "700" : "500",
                        color:
                          activeStudioTab === tab.id
                            ? "var(--color-1, #f09a21)"
                            : "#64748b",
                        borderBottom:
                          activeStudioTab === tab.id
                            ? "2px solid var(--color-1, #f09a21)"
                            : "2px solid transparent",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "6px",
                      }}
                    >
                      <i className={`fa-solid ${tab.icon}`} />
                      <span>{tab.label}</span>
                    </button>
                  ))}
                </div>

                {/* Tab 1: Name Field Configuration */}
                {activeStudioTab === "name" && (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "0.75rem",
                      fontSize: "0.85rem",
                    }}
                  >
                    {/* Bounding Box Sliders / Exact Inputs */}
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "0.5rem",
                      }}
                    >
                      <div>
                        <label style={{ color: "#64748b" }}>X Offset (%)</label>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.5"
                          value={nameBox.x ?? ""}
                          onChange={(e) => {
                            const val = e.target.value;
                            setNameBox((prev) => ({
                              ...prev,
                              x: val === "" ? "" : parseFloat(val),
                            }));
                          }}
                          onBlur={() => {
                            if (nameBox.x === "" || isNaN(Number(nameBox.x))) {
                              setNameBox((prev) => ({ ...prev, x: 0 }));
                            }
                          }}
                          style={{
                            width: "100%",
                            padding: "6px",
                            borderRadius: "4px",
                            border: "1px solid #d1d5db",
                          }}
                        />
                      </div>
                      <div>
                        <label style={{ color: "#64748b" }}>Y Offset (%)</label>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.5"
                          value={nameBox.y ?? ""}
                          onChange={(e) => {
                            const val = e.target.value;
                            setNameBox((prev) => ({
                              ...prev,
                              y: val === "" ? "" : parseFloat(val),
                            }));
                          }}
                          onBlur={() => {
                            if (nameBox.y === "" || isNaN(Number(nameBox.y))) {
                              setNameBox((prev) => ({ ...prev, y: 0 }));
                            }
                          }}
                          style={{
                            width: "100%",
                            padding: "6px",
                            borderRadius: "4px",
                            border: "1px solid #d1d5db",
                          }}
                        />
                      </div>
                      <div>
                        <label style={{ color: "#64748b" }}>Width (%)</label>
                        <input
                          type="number"
                          min="1"
                          max="100"
                          step="0.5"
                          value={nameBox.width ?? ""}
                          onChange={(e) => {
                            const val = e.target.value;
                            setNameBox((prev) => ({
                              ...prev,
                              width: val === "" ? "" : parseFloat(val),
                            }));
                          }}
                          onBlur={() => {
                            if (
                              nameBox.width === "" ||
                              isNaN(Number(nameBox.width)) ||
                              Number(nameBox.width) <= 0
                            ) {
                              setNameBox((prev) => ({ ...prev, width: 10 }));
                            }
                          }}
                          style={{
                            width: "100%",
                            padding: "6px",
                            borderRadius: "4px",
                            border: "1px solid #d1d5db",
                          }}
                        />
                      </div>
                      <div>
                        <label style={{ color: "#64748b" }}>Height (%)</label>
                        <input
                          type="number"
                          min="1"
                          max="100"
                          step="0.5"
                          value={nameBox.height ?? ""}
                          onChange={(e) => {
                            const val = e.target.value;
                            setNameBox((prev) => ({
                              ...prev,
                              height: val === "" ? "" : parseFloat(val),
                            }));
                          }}
                          onBlur={() => {
                            if (
                              nameBox.height === "" ||
                              isNaN(Number(nameBox.height)) ||
                              Number(nameBox.height) <= 0
                            ) {
                              setNameBox((prev) => ({ ...prev, height: 5 }));
                            }
                          }}
                          style={{
                            width: "100%",
                            padding: "6px",
                            borderRadius: "4px",
                            border: "1px solid #d1d5db",
                          }}
                        />
                      </div>
                    </div>

                    {/* Font Family */}
                    <div>
                      <label
                        style={{
                          fontWeight: "600",
                          color: "#334155",
                          display: "block",
                          marginBottom: "4px",
                        }}
                      >
                        Font Family
                      </label>
                      <Select
                        options={FONT_FAMILIES}
                        value={
                          FONT_FAMILIES.find(
                            (f) =>
                              f.value.toLowerCase() ===
                              (nameStyle.fontFamily || "").toLowerCase(),
                          ) || FONT_FAMILIES[0]
                        }
                        onChange={(opt) =>
                          setNameStyle({
                            ...nameStyle,
                            fontFamily: opt?.value || "Montserrat",
                          })
                        }
                        styles={fontSelectCustomStyles}
                      />
                    </div>

                    {/* Font Size & Weight */}
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "0.5rem",
                      }}
                    >
                      <div>
                        <label style={{ fontWeight: "600", color: "#334155" }}>
                          Font Size (px)
                        </label>
                        <input
                          type="number"
                          min="8"
                          max="300"
                          value={nameStyle.fontSize ?? ""}
                          onChange={(e) => {
                            const val = e.target.value;
                            setNameStyle({
                              ...nameStyle,
                              fontSize:
                                val === ""
                                  ? ""
                                  : isNaN(parseInt(val, 10))
                                    ? ""
                                    : parseInt(val, 10),
                            });
                          }}
                          onBlur={() => {
                            if (
                              nameStyle.fontSize === "" ||
                              isNaN(Number(nameStyle.fontSize)) ||
                              Number(nameStyle.fontSize) < 1
                            ) {
                              setNameStyle((prev) => ({
                                ...prev,
                                fontSize: 48,
                              }));
                            }
                          }}
                          style={{
                            width: "100%",
                            padding: "6px",
                            borderRadius: "4px",
                            border: "1px solid #d1d5db",
                          }}
                        />
                      </div>

                      <div>
                        <label style={{ fontWeight: "600", color: "#334155" }}>
                          Weight
                        </label>
                        <select
                          value={nameStyle.fontWeight}
                          onChange={(e) =>
                            setNameStyle({
                              ...nameStyle,
                              fontWeight: e.target.value,
                            })
                          }
                          style={{
                            width: "100%",
                            padding: "6px",
                            borderRadius: "4px",
                            border: "1px solid #d1d5db",
                            height: "34px",
                          }}
                        >
                          <option value="normal">Normal (400)</option>
                          <option value="500">Medium (500)</option>
                          <option value="600">Semi-Bold (600)</option>
                          <option value="bold">Bold (700)</option>
                          <option value="800">Extra Bold (800)</option>
                        </select>
                      </div>
                    </div>

                    {/* Text Alignment & Style */}
                    <div style={{ display: "flex", gap: "0.5rem" }}>
                      <div style={{ flex: 1 }}>
                        <label
                          style={{
                            fontWeight: "600",
                            color: "#334155",
                            display: "block",
                            marginBottom: "4px",
                          }}
                        >
                          Align
                        </label>
                        <div
                          style={{
                            display: "flex",
                            borderRadius: "6px",
                            overflow: "hidden",
                            border: "1px solid #d1d5db",
                          }}
                        >
                          {["left", "center", "right"].map((align) => (
                            <button
                              key={align}
                              type="button"
                              onClick={() =>
                                setNameStyle({ ...nameStyle, textAlign: align })
                              }
                              style={{
                                flex: 1,
                                padding: "6px",
                                border: "none",
                                background:
                                  nameStyle.textAlign === align
                                    ? "var(--color-1, #f09a21)"
                                    : "#ffffff",
                                color:
                                  nameStyle.textAlign === align
                                    ? "#ffffff"
                                    : "#475569",
                                cursor: "pointer",
                              }}
                            >
                              <i className={`fa-solid fa-align-${align}`} />
                            </button>
                          ))}
                        </div>
                      </div>

                      <div style={{ flex: 1 }}>
                        <label
                          style={{
                            fontWeight: "600",
                            color: "#334155",
                            display: "block",
                            marginBottom: "4px",
                          }}
                        >
                          Style
                        </label>
                        <button
                          type="button"
                          onClick={() =>
                            setNameStyle({
                              ...nameStyle,
                              fontStyle:
                                nameStyle.fontStyle === "italic"
                                  ? "normal"
                                  : "italic",
                            })
                          }
                          style={{
                            width: "100%",
                            padding: "6px",
                            borderRadius: "6px",
                            border: "1px solid #d1d5db",
                            background:
                              nameStyle.fontStyle === "italic"
                                ? "#eff6ff"
                                : "#ffffff",
                            color:
                              nameStyle.fontStyle === "italic"
                                ? "#2563eb"
                                : "#475569",
                            fontWeight: "600",
                            cursor: "pointer",
                          }}
                        >
                          <i className="fa-solid fa-italic" /> Italic
                        </button>
                      </div>
                    </div>

                    {/* Color Swatches */}
                    <div>
                      <label
                        style={{
                          fontWeight: "600",
                          color: "#334155",
                          display: "block",
                          marginBottom: "4px",
                        }}
                      >
                        Text Color: {nameStyle.color}
                      </label>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          flexWrap: "wrap",
                        }}
                      >
                        <input
                          type="color"
                          value={nameStyle.color}
                          onChange={(e) =>
                            setNameStyle({
                              ...nameStyle,
                              color: e.target.value,
                            })
                          }
                          style={{
                            width: "32px",
                            height: "32px",
                            padding: 0,
                            border: "none",
                            borderRadius: "4px",
                            cursor: "pointer",
                          }}
                        />
                        {PRESET_COLORS.map((preset) => (
                          <button
                            key={preset.value}
                            type="button"
                            onClick={() =>
                              setNameStyle({
                                ...nameStyle,
                                color: preset.value,
                              })
                            }
                            style={{
                              width: "22px",
                              height: "22px",
                              borderRadius: "4px",
                              backgroundColor: preset.value,
                              border:
                                nameStyle.color === preset.value
                                  ? "2px solid #2563eb"
                                  : "1px solid #cbd5e1",
                              cursor: "pointer",
                            }}
                            title={preset.label}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Tab 2: QR Code Configuration */}
                {activeStudioTab === "qr" && (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "0.75rem",
                      fontSize: "0.85rem",
                    }}
                  >
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "0.5rem",
                      }}
                    >
                      <div>
                        <label style={{ color: "#64748b" }}>X Offset (%)</label>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.5"
                          value={qrBox.x ?? ""}
                          onChange={(e) => {
                            const val = e.target.value;
                            setQrBox((prev) => ({
                              ...prev,
                              x: val === "" ? "" : parseFloat(val),
                            }));
                          }}
                          onBlur={() => {
                            if (qrBox.x === "" || isNaN(Number(qrBox.x))) {
                              setQrBox((prev) => ({ ...prev, x: 0 }));
                            }
                          }}
                          style={{
                            width: "100%",
                            padding: "6px",
                            borderRadius: "4px",
                            border: "1px solid #d1d5db",
                          }}
                        />
                      </div>
                      <div>
                        <label style={{ color: "#64748b" }}>Y Offset (%)</label>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.5"
                          value={qrBox.y ?? ""}
                          onChange={(e) => {
                            const val = e.target.value;
                            setQrBox((prev) => ({
                              ...prev,
                              y: val === "" ? "" : parseFloat(val),
                            }));
                          }}
                          onBlur={() => {
                            if (qrBox.y === "" || isNaN(Number(qrBox.y))) {
                              setQrBox((prev) => ({ ...prev, y: 0 }));
                            }
                          }}
                          style={{
                            width: "100%",
                            padding: "6px",
                            borderRadius: "4px",
                            border: "1px solid #d1d5db",
                          }}
                        />
                      </div>
                      <div style={{ gridColumn: "span 2" }}>
                        <label style={{ color: "#64748b" }}>
                          QR Size (% of Image Width)
                        </label>
                        <input
                          type="number"
                          min="2"
                          max="50"
                          step="0.5"
                          value={qrBox.size ?? ""}
                          onChange={(e) => {
                            const val = e.target.value;
                            setQrBox((prev) => ({
                              ...prev,
                              size: val === "" ? "" : parseFloat(val),
                            }));
                          }}
                          onBlur={() => {
                            if (
                              qrBox.size === "" ||
                              isNaN(Number(qrBox.size)) ||
                              Number(qrBox.size) <= 0
                            ) {
                              setQrBox((prev) => ({ ...prev, size: 10 }));
                            }
                          }}
                          style={{
                            width: "100%",
                            padding: "6px",
                            borderRadius: "4px",
                            border: "1px solid #d1d5db",
                          }}
                        />
                      </div>
                    </div>

                    <div>
                      <label
                        style={{
                          fontWeight: "600",
                          color: "#334155",
                          display: "block",
                          marginBottom: "4px",
                        }}
                      >
                        QR Module Color (Dark)
                      </label>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <input
                          type="color"
                          value={qrColors.darkColor}
                          onChange={(e) =>
                            setQrColors({
                              ...qrColors,
                              darkColor: e.target.value,
                            })
                          }
                          style={{
                            width: "32px",
                            height: "32px",
                            padding: 0,
                            border: "none",
                            borderRadius: "4px",
                            cursor: "pointer",
                          }}
                        />
                        <input
                          type="text"
                          value={qrColors.darkColor}
                          onChange={(e) =>
                            setQrColors({
                              ...qrColors,
                              darkColor: e.target.value,
                            })
                          }
                          style={{
                            flex: 1,
                            padding: "6px",
                            borderRadius: "4px",
                            border: "1px solid #d1d5db",
                          }}
                        />
                      </div>
                    </div>

                    <div>
                      <label
                        style={{
                          fontWeight: "600",
                          color: "#334155",
                          display: "block",
                          marginBottom: "4px",
                        }}
                      >
                        QR Background Color (Light)
                      </label>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <input
                          type="color"
                          value={qrColors.lightColor}
                          onChange={(e) =>
                            setQrColors({
                              ...qrColors,
                              lightColor: e.target.value,
                            })
                          }
                          style={{
                            width: "32px",
                            height: "32px",
                            padding: 0,
                            border: "none",
                            borderRadius: "4px",
                            cursor: "pointer",
                          }}
                        />
                        <input
                          type="text"
                          value={qrColors.lightColor}
                          onChange={(e) =>
                            setQrColors({
                              ...qrColors,
                              lightColor: e.target.value,
                            })
                          }
                          style={{
                            flex: 1,
                            padding: "6px",
                            borderRadius: "4px",
                            border: "1px solid #d1d5db",
                          }}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Tab 3: Sample Preview Settings */}
                {activeStudioTab === "preview" && (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "0.75rem",
                      fontSize: "0.85rem",
                    }}
                  >
                    <div>
                      <label
                        style={{
                          fontWeight: "600",
                          color: "#334155",
                          display: "block",
                          marginBottom: "4px",
                        }}
                      >
                        Sample Student Name (Live Overlay)
                      </label>
                      <input
                        type="text"
                        value={previewSampleName}
                        onChange={(e) => setPreviewSampleName(e.target.value)}
                        placeholder="e.g., JANE DOE"
                        style={{
                          width: "100%",
                          padding: "8px",
                          borderRadius: "6px",
                          border: "1px solid #d1d5db",
                        }}
                      />
                    </div>

                    <p style={{ color: "#64748b", margin: 0, lineHeight: 1.4 }}>
                      Tip: Test short and long student names to make sure they
                      wrap comfortably within the bounding box.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* ─────────────────────────────────────────────────────────────
          SERVER LIVE PREVIEW MODAL
      ───────────────────────────────────────────────────────────── */}
      {isPreviewModalOpen && (
        <Modal
          isOpen={isPreviewModalOpen}
          onClose={() => setIsPreviewModalOpen(false)}
          maxWidth="960px"
        >
          <div
            style={{ display: "flex", flexDirection: "column", gap: "1rem" }}
          >
            {isPreviewLoading ? (
              <div style={{ textAlign: "center", padding: "3rem" }}>
                <Loader style={{ fontSize: "36px" }} />
                <p style={{ marginTop: "1rem", color: "#64748b", left: "0" }}>
                  Generating PNG preview from backend...
                </p>
              </div>
            ) : previewImageBase64 ? (
              <div style={{ textAlign: "center" }}>
                <img
                  src={previewImageBase64}
                  alt="Certificate Preview"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "70vh",
                    objectFit: "contain",
                    borderRadius: "8px",
                    boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
                    border: "1px solid #e2e8f0",
                  }}
                />
              </div>
            ) : (
              <p style={{ color: "#ef4444" }}>
                Could not render preview image.
              </p>
            )}
          </div>
        </Modal>
      )}

      {/* ─────────────────────────────────────────────────────────────
          ISSUED CERTIFICATES MANAGEMENT MODAL
      ───────────────────────────────────────────────────────────── */}
      {isIssuedModalOpen && (
        <Modal
          isOpen={isIssuedModalOpen}
          onClose={() => setIsIssuedModalOpen(false)}
          maxWidth="1100px"
        >
          <div
            style={{ display: "flex", flexDirection: "column", gap: "1rem" }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                borderBottom: "1px solid #e2e8f0",
                paddingBottom: "0.75rem",
              }}
            >
              <div>
                <h3
                  style={{
                    margin: 0,
                    fontSize: "1.25rem",
                    color: "#0f172a",
                    fontWeight: "700",
                  }}
                >
                  Issued Certificates: {currentTemplateForIssued?.name}
                </h3>
                <p
                  style={{
                    margin: "2px 0 0 0",
                    color: "#64748b",
                    fontSize: "0.85rem",
                  }}
                >
                  Review active and revoked certificates issued using this
                  template.
                </p>
              </div>

              <span
                style={{
                  fontSize: "0.85rem",
                  fontWeight: "600",
                  color: "#475569",
                  backgroundColor: "#f1f5f9",
                  padding: "4px 10px",
                  borderRadius: "12px",
                }}
              >
                Total: {issuedCerts.length} Issued
              </span>
            </div>

            {isIssuedLoading ? (
              <div style={{ textAlign: "center", padding: "3rem" }}>
                <Loader style={{ fontSize: "36px", left: "0" }} />
              </div>
            ) : issuedCerts.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "3rem",
                  color: "#64748b",
                }}
              >
                <i
                  className="fa-solid fa-award"
                  style={{
                    fontSize: "2.5rem",
                    color: "#cbd5e1",
                    marginBottom: "0.75rem",
                  }}
                />
                <p style={{ margin: 0 }}>
                  No certificates have been issued yet with this template.
                </p>
              </div>
            ) : (
              <div style={{ overflowX: "auto", maxHeight: "60vh" }}>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: "0.88rem",
                    textAlign: "left",
                  }}
                >
                  <thead>
                    <tr
                      style={{
                        backgroundColor: "#f8fafc",
                        borderBottom: "2px solid #e2e8f0",
                      }}
                    >
                      <th
                        style={{
                          padding: "10px 12px",
                          color: "#475569",
                          fontWeight: "600",
                        }}
                      >
                        Student Name
                      </th>
                      <th
                        style={{
                          padding: "10px 12px",
                          color: "#475569",
                          fontWeight: "600",
                        }}
                      >
                        Verification Code
                      </th>
                      <th
                        style={{
                          padding: "10px 12px",
                          color: "#475569",
                          fontWeight: "600",
                        }}
                      >
                        Issued At
                      </th>
                      <th
                        style={{
                          padding: "10px 12px",
                          color: "#475569",
                          fontWeight: "600",
                        }}
                      >
                        Status
                      </th>
                      <th
                        style={{
                          padding: "10px 12px",
                          color: "#475569",
                          fontWeight: "600",
                          textAlign: "right",
                        }}
                      >
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {issuedCerts.map((cert) => (
                      <tr
                        key={cert._id}
                        style={{ borderBottom: "1px solid #f1f5f9" }}
                      >
                        <td
                          style={{
                            padding: "10px 12px",
                            fontWeight: "600",
                            color: "#0f172a",
                          }}
                        >
                          {cert.studentName}
                          {cert.studentId?.email && (
                            <span
                              style={{
                                display: "block",
                                fontSize: "0.78rem",
                                color: "#64748b",
                                fontWeight: "normal",
                              }}
                            >
                              {cert.studentId.email}
                            </span>
                          )}
                        </td>
                        <td style={{ padding: "10px 12px" }}>
                          <span
                            style={{
                              fontFamily: "monospace",
                              backgroundColor: "#f1f5f9",
                              padding: "2px 6px",
                              borderRadius: "4px",
                              fontSize: "0.85rem",
                            }}
                          >
                            {cert.verificationCode}
                          </span>
                        </td>
                        <td style={{ padding: "10px 12px", color: "#64748b" }}>
                          {new Date(cert.issuedAt).toLocaleDateString()}
                        </td>
                        <td style={{ padding: "10px 12px" }}>
                          {cert.isRevoked ? (
                            <span
                              style={{
                                color: "#dc2626",
                                fontWeight: "600",
                                backgroundColor: "#fee2e2",
                                padding: "2px 8px",
                                borderRadius: "12px",
                                fontSize: "0.75rem",
                              }}
                            >
                              Revoked
                            </span>
                          ) : (
                            <span
                              style={{
                                color: "#16a34a",
                                fontWeight: "600",
                                backgroundColor: "#dcfce7",
                                padding: "2px 8px",
                                borderRadius: "12px",
                                fontSize: "0.75rem",
                              }}
                            >
                              Valid
                            </span>
                          )}
                        </td>
                        <td
                          style={{ padding: "10px 12px", textAlign: "right" }}
                        >
                          <div style={{ display: "inline-flex", gap: "6px" }}>
                            {!cert.isRevoked && (
                              <button
                                onClick={() => handleOpenRevoke(cert)}
                                style={{
                                  padding: "4px 8px",
                                  borderRadius: "4px",
                                  border: "1px solid #fee2e2",
                                  backgroundColor: "#fff1f2",
                                  color: "#dc2626",
                                  fontSize: "0.78rem",
                                  fontWeight: "600",
                                  cursor: "pointer",
                                }}
                              >
                                Revoke
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* ─────────────────────────────────────────────────────────────
          REVOKE CONFIRMATION MODAL
      ───────────────────────────────────────────────────────────── */}
      {isRevokeModalOpen && (
        <Modal
          isOpen={isRevokeModalOpen}
          onClose={() => !isRevoking && setIsRevokeModalOpen(false)}
          maxWidth="460px"
        >
          <form
            onSubmit={handleConfirmRevoke}
            style={{ display: "flex", flexDirection: "column", gap: "1rem" }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                color: "#dc2626",
              }}
            >
              <i
                className="fa-solid fa-triangle-exclamation"
                style={{ fontSize: "1.4rem" }}
              />
              <h3 style={{ margin: 0, fontSize: "1.15rem", fontWeight: "700" }}>
                Revoke Certificate
              </h3>
            </div>

            <p style={{ margin: 0, fontSize: "0.9rem", color: "#475569" }}>
              Are you sure you want to revoke the certificate for{" "}
              <strong>{certToRevoke?.studentName}</strong>? Once revoked, any
              verification scan will indicate this certificate is void.
            </p>

            <div>
              <label
                style={{
                  display: "block",
                  fontSize: "0.85rem",
                  fontWeight: "600",
                  color: "#334155",
                  marginBottom: "4px",
                }}
              >
                Reason for Revocation (Optional)
              </label>
              <textarea
                value={revokeReasonInput}
                onChange={(e) => setRevokeReasonInput(e.target.value)}
                placeholder="e.g., Issued by mistake, exam misconduct..."
                rows={3}
                style={{
                  width: "100%",
                  padding: "8px",
                  borderRadius: "6px",
                  border: "1px solid #d1d5db",
                  fontSize: "0.88rem",
                }}
              />
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "0.75rem",
                marginTop: "0.5rem",
              }}
            >
              <button
                type="button"
                onClick={() => setIsRevokeModalOpen(false)}
                disabled={isRevoking}
                style={{
                  padding: "8px 14px",
                  borderRadius: "6px",
                  border: "1px solid #cbd5e1",
                  backgroundColor: "#ffffff",
                  color: "#475569",
                  fontWeight: "600",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isRevoking}
                style={{
                  padding: "8px 16px",
                  borderRadius: "6px",
                  border: "none",
                  backgroundColor: "#dc2626",
                  color: "#ffffff",
                  fontWeight: "600",
                  cursor: "pointer",
                }}
              >
                {isRevoking ? "Revoking..." : "Confirm Revoke"}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
